import { computed, signal } from '@angular/core';

/**
 * Generic authorization data structure
 */
export interface AuthorizationData<TRole extends string, TPermission extends string> {
  role: TRole | null;
  roleName: string | null;
  dashboardViewTypeName: string | null;
  permissions: TPermission[];
  isLoaded: boolean;
}

/**
 * Base class for authorization state management with type-safe roles and permissions.
 * Provides common functionality for managing user roles and permissions.
 *
 * @template TRole - The role enum type (e.g., CaseRole, InvestigationRole)
 * @template TPermission - The permission enum type (e.g., CasePermission, InvestigationPermission)
 */
export abstract class AuthorizationStateBase<TRole extends string, TPermission extends string> {
  protected readonly _state = signal<AuthorizationData<TRole, TPermission>>({
    role: null,
    roleName: null,
    dashboardViewTypeName: null,
    permissions: [],
    isLoaded: false
  });

  readonly role = computed(() => this._state().role);
  readonly roleName = computed(() => this._state().roleName);
  readonly dashboardViewTypeName = computed(() => this._state().dashboardViewTypeName);
  readonly permissions = computed(() => this._state().permissions);
  readonly isLoaded = computed(() => this._state().isLoaded);

  setAuthorization(role: TRole | null, permissions: TPermission[], roleName: string | null = null, dashboardViewTypeName: string | null = null): void {
    this._state.set({
      role,
      roleName,
      dashboardViewTypeName,
      permissions,
      isLoaded: true
    });
  }

  clear(): void {
    this._state.set({
      role: null,
      roleName: null,
      dashboardViewTypeName: null,
      permissions: [],
      isLoaded: false
    });
  }

  hasPermission(permission: TPermission): boolean {
    return this._state().permissions.includes(permission);
  }

  hasRole(role: TRole): boolean {
    return this._state().role === role;
  }
}
